import type { EvidenceLocator, EvidenceReference } from "@opencode-ai/session-ui/evidence-reference"
import type { DatoxExtraction, DatoxFile } from "./api"

export function enrichEvidenceReference(
  reference: EvidenceReference,
  extractions: DatoxExtraction[],
  files: DatoxFile[],
) {
  const file = files.find((item) => item.id === reference.fileID)
  const evidence = reference.evidence?.length
    ? reference.evidence
    : extractionEvidence(reference, extractions, file)
  if (reference.fileName === file?.filename && sameEvidence(reference.evidence, evidence)) return reference
  return { ...reference, fileName: file?.filename ?? reference.fileName, evidence }
}

function extractionEvidence(reference: EvidenceReference, extractions: DatoxExtraction[], file?: DatoxFile) {
  if (!file) return reference.evidence
  const targetTable = normalize(reference.extractedTable)
  const locators = extractions.flatMap((extraction) => {
    const result = extraction.result_json
    const tables = Array.isArray(result.tables) ? result.tables : []
    return tables.flatMap((table) => {
      if (!table || typeof table !== "object") return []
      const value = table as Record<string, unknown>
      if (targetTable && normalize(tableName(value)) !== targetTable) return []
      const rows = Array.isArray(value.rows) ? value.rows.filter(isRecord) : []
      const associated = extraction.file_id === file.id
        || (Array.isArray(result.source_file_ids) && result.source_file_ids.includes(file.id))
        || rows.some((row) => rowMatchesFile(row, file))
      if (!associated) return []
      return rows.filter((row) => rowMatchesFile(row, file) || extraction.file_id === file.id).flatMap(rowLocator)
    })
  })
  return dedupeLocators(locators)
}

function rowLocator(row: Record<string, unknown>) {
  const locator = {
    page: numeric(row.page ?? row.source_page),
    quote: string(row.quote ?? row.source_quote),
    sheet: string(row.source_sheet ?? row.sheet),
    cell_range: string(row.source_cell_range ?? row.cell_range),
    row: numeric(row.source_row),
  }
  return Object.values(locator).some((value) => value !== undefined) ? [locator] : []
}

function rowMatchesFile(row: Record<string, unknown>, file: DatoxFile) {
  if (row.source_file_id === file.id || row.file_id === file.id) return true
  const name = string(row.source_file ?? row.source_filename ?? row.filename)
  return !!name && basename(name) === file.filename.toLocaleLowerCase()
}

function tableName(table: Record<string, unknown>) {
  return string(table.name ?? table.table_name ?? table.title)
}

function normalize(value: unknown) {
  return String(value ?? "").trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, "")
}

function basename(value: string) {
  return value.trim().replaceAll("\\", "/").split("/").at(-1)?.toLocaleLowerCase() ?? ""
}

function numeric(value: unknown) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : undefined
}

function string(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function dedupeLocators(values: EvidenceLocator[]) {
  const seen = new Set<string>()
  return values.filter((value) => {
    const key = JSON.stringify(value)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function sameEvidence(first?: EvidenceLocator[], second?: EvidenceLocator[]) {
  return JSON.stringify(first ?? []) === JSON.stringify(second ?? [])
}
